/**
 * GgCartToast — the single, global "added to cart" toast.
 *
 * Rendered once inside the header bar, which it covers while it shows
 * (snippets/gg-cart-toast.liquid). Any
 * add-to-cart path dispatches a `gg:cart-toast` event on `document` carrying the
 * added item's display data; this element renders + animates it. Decoupled from any single buy button so PDP, quick-add, etc.
 * all resolve to the same animation.
 *
 * Event contract:
 *   document.dispatchEvent(new CustomEvent('gg:cart-toast', {
 *     detail: { title: string, brand?: string, thumb?: string }
 *   }));
 */

const EVENT_NAME = 'gg:cart-toast';

class GgCartToast extends HTMLElement {
  /** @type {HTMLImageElement | null} */
  #thumb = null;
  /** @type {HTMLElement | null} */
  #brand = null;
  /** @type {HTMLElement | null} */
  #title = null;

  connectedCallback() {
    this.#thumb = this.querySelector('.gg-cart-toast__thumb');
    this.#brand = this.querySelector('.gg-cart-toast__brand');
    this.#title = this.querySelector('.gg-cart-toast__title');

    this.addEventListener('animationend', this.#onAnimationEnd);
    document.addEventListener(EVENT_NAME, this.#onToast);
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT_NAME, this.#onToast);
    this.removeEventListener('animationend', this.#onAnimationEnd);
  }

  /** @param {Event} event */
  #onToast = (event) => {
    const { title, brand, thumb } = /** @type {CustomEvent} */ (event).detail || {};

    if (this.#title) this.#title.textContent = title || '';

    if (this.#brand) {
      if (brand) {
        this.#brand.textContent = brand;
        this.#brand.hidden = false;
      } else {
        this.#brand.textContent = '';
        this.#brand.hidden = true;
      }
    }

    if (this.#thumb) {
      if (thumb) {
        this.#thumb.src = thumb;
        this.#thumb.hidden = false;
      } else {
        this.#thumb.removeAttribute('src');
        this.#thumb.hidden = true;
      }
    }

    this.#show();
  };

  // The choreography (in → hold → out) lives in CSS — an -in and an -out
  // animation with nothing running during the hold (see gg-cart-toast.liquid).
  // We just (re)trigger the class.
  #show() {
    this.removeAttribute('data-hidden');
    this.classList.remove('is-visible');
    void this.offsetWidth; // reflow so the animation restarts on rapid adds
    this.classList.add('is-visible');
  }

  /** @param {AnimationEvent} event */
  #onAnimationEnd = (event) => {
    if (event.target !== this) return; // ignore child animations
    // Only the toast's own -out animation ends it (the -in one also fires
    // animationend, 0.5s in) — never a stray/bubbled one.
    if (!/^gg-cart-toast(-fade)?-out$/.test(event.animationName)) return;
    this.classList.remove('is-visible');
    this.setAttribute('data-hidden', 'true');
  };

}

if (!customElements.get('gg-cart-toast')) {
  customElements.define('gg-cart-toast', GgCartToast);
}
